// Product detail page controller, mirrors the storefront PDP BuyBox/Gallery/
// CompleteSet/StickyBuyBar behaviour on top of Shopify data.
const money = (c) =>
  (c / 100).toLocaleString('nl-NL', {
    style: 'currency',
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
    minimumFractionDigits: 2,
  });

const pct = (now, was) => (!was || was <= now ? 0 : Math.round(((was - now) / was) * 100));

const TRUCK =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';

class ProductDetail extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.freeShip = Number(this.dataset.freeShipping || 5999);
    this.qty = 1;

    // Voorraad per variant staat in een eigen blok: de variant-JSON van Shopify
    // bevat geen inventory_quantity.
    const stockEl = this.querySelector('[data-stock-map]');
    this.stockMap = stockEl ? JSON.parse(stockEl.textContent) : {};
    this.lowStock = Number(this.dataset.lowStock || 0);
    this.stockLine = this.querySelector('[data-stock-line]');
    this.stockLabel = this.stockLine?.querySelector('[data-stock-label]');

    this.priceEl = this.querySelector('[data-price]');
    this.wasEl = this.querySelector('[data-was]');
    this.saveEl = this.querySelector('[data-save]');
    this.badgeEl = this.querySelector('[data-off-badge]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtyOut = this.querySelector('[data-qty-out]');
    this.addBtn = this.querySelector('[data-add]');
    this.taxEl = this.querySelector('[data-tax]');
    this.plan = this.initSellingPlan();

    this.addEventListener('change', (e) => {
      if (e.target.matches('input[name^="option"]')) this.onOption();
      if (e.target.matches('[data-set-check]')) this.renderSet();
      if (e.target.name === 'purchase_option') {
        this.plan?.onSelect(e.target);
        this.syncQty();
      }
    });
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-step]');
      if (step) {
        this.qty = Math.max(1, this.qty + Number(step.dataset.step));
        this.syncQty();
      }
      if (e.target.closest('[data-set-add]')) this.addSet(e);

      // Maattabel-modal. De klik op de achtergrond komt binnen met de <dialog>
      // zelf als target, want het paneel eromheen vangt de rest af.
      const chart = this.querySelector('[data-size-chart]');
      if (chart) {
        if (e.target.closest('[data-size-chart-open]')) chart.showModal();
        if (e.target.closest('[data-size-chart-close]') || e.target === chart) chart.close();
      }
    });

    document.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      this.querySelector('#product-form')?.requestSubmit();
    });

    // Extra-abonnement: cart.js post het formulier als één line item, dus het
    // abonnement kan daar niet in mee. De listener hangt aan het formulier zelf
    // en loopt daarmee vóór de document-listener van cart.js.
    this.extraCheck = this.querySelector('[data-extra-check]');
    if (this.extraCheck) {
      this.querySelector('#product-form')?.addEventListener('submit', (e) => this.addWithExtra(e));
    }

    this.initGallery();
    this.onOption();
    this.initSticky();
  }

  currentVariant() {
    // Map each *rendered* option (by 1-based position) to its checked value.
    // Single-value options aren't rendered, so match only on the chosen ones.
    const chosen = {};
    this.querySelectorAll('input[name^="option"]:checked').forEach((i) => {
      chosen[Number(i.name.replace('option', ''))] = i.value;
    });
    const keys = Object.keys(chosen);
    if (!keys.length) return this.variants[0];
    return (
      this.variants.find((v) => keys.every((pos) => v.options[pos - 1] === chosen[pos])) ||
      this.variants[0]
    );
  }

  onOption() {
    const v = this.currentVariant();
    if (!v) return;
    this.variant = v;
    this.idInput.value = v.id;
    this.plan?.updateForVariant(v.id);
    this.showVariantMedia(v);

    // Reflect selected value in each option legend.
    this.querySelectorAll('[data-opt-value]').forEach((span) => {
      const pos = span.dataset.optValue;
      const checked = this.querySelector(`input[name="option${pos}"]:checked`);
      if (checked) span.textContent = checked.value;
    });

    // Laat de maattabel dezelfde maat markeren als de maatchips. De positie van
    // de maatoptie staat op de <dialog>, zodat dit ook klopt bij producten waar
    // maat niet de eerste optie is.
    const chart = this.querySelector('[data-size-chart][data-size-pos]');
    if (chart) {
      const size = this.querySelector(`input[name="option${chart.dataset.sizePos}"]:checked`)?.value;
      chart.querySelectorAll('[data-size-row]').forEach((row) => {
        row.classList.toggle('is-current', row.dataset.sizeRow === size);
      });
    }

    // Het afmetingenblok heeft per variant een lijst klaarstaan; toon die van de
    // gekozen variant. Varianten zonder toymaat hebben geen lijst, dan blijft het
    // blok op de vorige staan.
    const dims = this.querySelector('[data-dimensions]');
    if (dims) {
      const wanted = dims.querySelector(`[data-dims-variant="${v.id}"]`);
      if (wanted) {
        dims.querySelectorAll('[data-dims-variant]').forEach((list) => {
          list.hidden = list !== wanted;
        });
      }
    }

    const price = v.price;
    const compare = v.compare_at_price;
    const off = pct(price, compare);
    const sale = off > 0;

    this.priceEl.textContent = money(price);
    this.priceEl.className = sale ? 'pdp-priceSale' : 'pdp-price';
    if (this.wasEl) {
      this.wasEl.textContent = compare ? money(compare) : '';
      this.wasEl.hidden = !sale;
    }
    if (this.badgeEl) {
      this.badgeEl.textContent = `−${off}%`;
      this.badgeEl.hidden = !sale;
    }
    if (this.saveEl) {
      this.saveEl.textContent = sale ? `Je bespaart ${money(compare - price)}` : '';
      this.saveEl.hidden = !sale;
    }
    this.syncQty();
    this.syncStock();
    this.renderSet();
  }

  // Houdt de voorraadregel gelijk aan de gekozen variant. Spiegelt de staten uit
  // snippets/stock-level.liquid, inclusief de gate op getrackte voorraad, een
  // niet-getrackte variant rapporteert -1 en mag nooit een aantal tonen.
  syncStock() {
    if (!this.stockLine || !this.stockLabel) return;
    const v = this.variant;
    if (!v) return;
    const stock = this.stockMap[String(v.id)];
    let state = 'ok';
    let text = 'Op voorraad. Werkdagen vóór 15:00 besteld, morgen in huis.';
    if (!v.available) {
      state = 'oos';
      text = 'Tijdelijk uitverkocht';
    } else if (stock && stock.t && stock.q > 0 && stock.q <= this.lowStock) {
      state = 'low';
      text = `Nog ${stock.q} op voorraad`;
    }
    this.stockLine.dataset.stockState = state;
    this.stockLabel.textContent = text;
  }

  syncQty() {
    if (this.qtyOut) this.qtyOut.textContent = this.qty;
    if (this.qtyInput) this.qtyInput.value = this.qty;
    const v = this.variant;
    const inStock = v ? v.available : true;
    const unit = this.plan && this.plan.selectedPrice != null ? this.plan.selectedPrice : v ? v.price : 0;
    this.addBtn.disabled = !inStock;
    const addLabel = this.dataset.addLabel || 'In je nachtkastje';
    this.addBtn.textContent = inStock ? `${addLabel} · ${money(unit * this.qty)}` : 'Uitverkocht';
    if (this.taxEl) {
      const total = unit * this.qty;
      this.taxEl.innerHTML =
        total >= this.freeShip
          ? `<span class="pdp-free">${TRUCK} Gratis verzending inbegrepen</span>`
          : `<span class="pdp-free">gratis verzending vanaf ${money(this.freeShip)}</span>`;
    }
  }

  // ── Subscription / selling plan ──
  // Renders nothing when the product has no selling plans (snippet gates on it),
  // so this returns null and price logic falls back to the plain variant price.
  initSellingPlan() {
    const el = this.querySelector('[data-selling-plan-picker]');
    if (!el) return null;
    const input = el.querySelector('[data-selling-plan-input]');
    const requires = el.dataset.requiresSellingPlan === 'true';
    const allocations = JSON.parse(el.querySelector('[data-selling-plan-allocations]').textContent);

    const ctl = {
      selectedPrice: null,
      // Show only the plans allocated to the current variant, refresh their
      // prices, keep the checked option valid, and sync the hidden input.
      updateForVariant: (variantId) => {
        const list = allocations[String(variantId)] || [];
        const byId = new Map(list.map((a) => [String(a.id), a]));
        el.querySelectorAll('[data-selling-plan-option]').forEach((opt) => {
          const a = byId.get(opt.dataset.sellingPlanOption);
          opt.classList.toggle('hidden', !a);
          opt.querySelector('input').disabled = !a;
          const price = opt.querySelector('[data-selling-plan-price]');
          if (price && a) price.firstChild.textContent = money(a.price) + ' ';
        });

        let sel = el.querySelector('input[name="purchase_option"]:checked');
        if (!sel || sel.disabled || (sel.value !== '' && !byId.has(sel.value))) {
          const fallback = requires
            ? el.querySelector('[data-selling-plan-option]:not(.hidden) input')
            : el.querySelector('input[name="purchase_option"][value=""]');
          if (fallback) {
            fallback.checked = true;
            sel = fallback;
          }
        }
        ctl.selectedPrice = sel && sel.value && byId.has(sel.value) ? byId.get(sel.value).price : null;
        input.value = sel ? sel.value : '';
      },
      // React to the shopper picking one-time vs a subscription plan.
      onSelect: (radio) => {
        input.value = radio.value;
        const list = allocations[String(this.variant.id)] || [];
        const a = list.find((x) => String(x.id) === radio.value);
        ctl.selectedPrice = a ? a.price : null;
      },
    };
    return ctl;
  }

  // ── Complete the set ──
  renderSet() {
    const set = this.querySelector('[data-set]');
    if (!set) return;
    const checks = [...set.querySelectorAll('[data-set-check]')];
    const anchorPrice = this.variant ? this.variant.price : Number(set.dataset.anchorPrice || 0);
    const addonTotal = checks
      .filter((c) => c.checked)
      .reduce((s, c) => s + Number(c.dataset.price), 0);
    const total = anchorPrice + addonTotal;
    const count = checks.filter((c) => c.checked).length + 1;

    const tPrice = set.querySelector('[data-set-total]');
    const tLabel = set.querySelector('[data-set-count]');
    const btn = set.querySelector('[data-set-add]');
    const nudge = set.querySelector('[data-set-nudge]');
    if (tPrice) tPrice.textContent = money(total);
    if (tLabel) tLabel.textContent = `${count} ${count === 1 ? 'item' : 'items'} samen`;
    if (btn) btn.textContent = count === 1 ? 'Voeg toe aan mandje' : `Voeg ${count} items toe`;
    // Blijft altijd staan: een regel die verdwijnt laat de balk springen zodra
    // je een add-on uitvinkt. Alleen de toon verandert met het totaal.
    if (nudge) {
      const toFree = this.freeShip - total;
      nudge.hidden = false;
      if (total >= this.freeShip) {
        nudge.className = 'pdp-setNudge pdp-setNudgeOk';
        nudge.innerHTML = `${TRUCK} Gratis verzending inbegrepen`;
      } else if (toFree <= 1500) {
        nudge.className = 'pdp-setNudge';
        nudge.innerHTML = `${TRUCK} Nog ${money(toFree)} tot gratis verzending`;
      } else {
        nudge.className = 'pdp-setNudge';
        nudge.innerHTML = `${TRUCK} Gratis verzending vanaf ${money(this.freeShip)}`;
      }
    }
  }

  async addSet(e) {
    e.preventDefault();
    const set = this.querySelector('[data-set]');
    // Quiz-antwoorden (properties[...]) horen bij het hoofdproduct, niet bij de set-items.
    const properties = this.formProperties();
    const main = { id: Number(this.idInput.value), quantity: this.qty };
    if (Object.keys(properties).length) main.properties = properties;
    const items = [main];
    set.querySelectorAll('[data-set-check]:checked').forEach((c) => {
      items.push({ id: Number(c.dataset.variant), quantity: 1 });
    });
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      window.showCartPopup?.([], d.description || d.message || 'Toevoegen mislukt');
      return;
    }
    window.hkAddToCart?.(d);
    await window.refreshCart?.();
    window.showCartPopup?.(d.items || [d]);
  }

  // Quiz-antwoorden en andere line item properties uit het productformulier.
  formProperties() {
    const properties = {};
    this.querySelectorAll('#product-form [name^="properties["]').forEach((f) => {
      if (f.type === 'radio' && !f.checked) return;
      if (!f.value) return;
      properties[f.name.slice(11, -1)] = f.value;
    });
    return properties;
  }

  // ── Extra-abonnement meebestellen ──
  async addWithExtra(e) {
    if (!this.extraCheck?.checked) return; // niet aangevinkt: normale pad in cart.js
    e.preventDefault();
    e.stopPropagation();
    const main = { id: Number(this.idInput.value), quantity: this.qty };
    const properties = this.formProperties();
    if (Object.keys(properties).length) main.properties = properties;
    const planInput = this.querySelector('[data-selling-plan-input]');
    if (planInput?.value) main.selling_plan = Number(planInput.value);

    const extra = { id: Number(this.extraCheck.dataset.variant), quantity: 1 };
    if (this.extraCheck.dataset.sellingPlan) extra.selling_plan = Number(this.extraCheck.dataset.sellingPlan);

    this.addBtn?.setAttribute('aria-busy', 'true');
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [main, extra] }),
    }).catch(() => null);
    this.addBtn?.removeAttribute('aria-busy');
    const d = await r?.json().catch(() => ({}));
    if (!r?.ok) {
      window.showCartPopup?.([], d?.description || d?.message || 'Toevoegen mislukt');
      return;
    }
    window.hkAddToCart?.(d);
    // Abonnement zit nu in de cart: blok weg, zodat het niet dubbel kan.
    this.extraCheck.checked = false;
    this.querySelector('[data-extra]')?.setAttribute('hidden', '');
    await window.refreshCart?.();
    window.showCartPopup?.(d.items || [d]);
  }

  // ── Gallery ──
  initGallery() {
    this.stageImgs = [...this.querySelectorAll('[data-stage-img]')];
    this.thumbBtns = [...this.querySelectorAll('[data-thumb]')];
    this.thumbBtns.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.activateMedia(i));
    });
  }

  activateMedia(i) {
    this.stageImgs.forEach((img, j) => img.toggleAttribute('data-active', i === j));
    this.thumbBtns.forEach((t, j) => t.setAttribute('aria-current', i === j ? 'true' : 'false'));
  }

  // Switch gallery to the variant's featured image when it has one.
  showVariantMedia(v) {
    if (!this.stageImgs || !v || !v.featured_media) return;
    const id = String(v.featured_media.id);
    const idx = this.stageImgs.findIndex((img) => img.dataset.mediaId === id);
    if (idx >= 0) this.activateMedia(idx);
  }

  // ── Sticky bar ──
  initSticky() {
    const bar = document.querySelector('[data-sticky-bar]');
    if (!bar) return;
    const onScroll = () => bar.toggleAttribute('data-show', window.scrollY > 640);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}
customElements.define('product-detail', ProductDetail);
